import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Image as ImageIcon, FileText, ExternalLink, Megaphone, Send, Star, Quote } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function Promo() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testimonials, setTestimonials] = useState([]);
  const [tf, setTf] = useState({ name: "", quote: "", rating: 5, consent: true });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/promo-materials").catch(() => ({ data: [] })),
      api.get("/testimonials").catch(() => ({ data: [] })),
    ]).then(([p, tt]) => {
      setItems(p.data || []);
      setTestimonials(tt.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const submitTestimonial = async (e) => {
    e.preventDefault();
    if (!tf.consent) return toast.error("Please consent so I may publish your words.");
    setSubmitting(true);
    try {
      await api.post("/testimonials", { ...tf, preferred_language: lang });
      toast.success("Thank you — I'll review and publish shortly.");
      setSubmitted(true);
      setTf({ name: "", quote: "", rating: 5, consent: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not submit.");
    } finally { setSubmitting(false); }
  };

  const href = (it) => it.external_url || it.file_data || "";
  const iconFor = (it) => {
    if (it.file_type === "pdf") return FileText;
    if (it.file_type === "link" || (!it.file_data && it.external_url)) return ExternalLink;
    return ImageIcon;
  };

  return (
    <div data-testid="promo-page" className="container-refined py-16 md:py-24">
      <div className="max-w-2xl">
        <p className="eyebrow"><Megaphone className="inline h-3.5 w-3.5 mr-1" strokeWidth={1.5}/> {t("nav.promo")}</p>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[1.05] text-forest-900">Share the mission</h1>
        <p className="mt-6 text-forest-700 leading-relaxed">
          A small library of posters, flyers and social visuals you can print, share or forward. Everything here is free to use in support of Sunbraider and Ukraine.
        </p>
      </div>

      {loading ? (
        <p className="mt-12 text-sm text-forest-700">Loading…</p>
      ) : items.length === 0 ? (
        <div className="mt-12 card-warm max-w-2xl" data-testid="promo-empty">
          <p className="font-serif text-2xl text-forest-900">Coming soon.</p>
          <p className="mt-3 text-sm text-forest-700">Isa is putting the first flyers together. Check back shortly — or ask on Signal / info@sunbraider.nl for a preview.</p>
        </div>
      ) : (
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it) => {
            const Icon = iconFor(it);
            const link = href(it);
            const isImage = it.file_type === "image" && it.file_data;
            return (
              <div key={it.id} data-testid={`promo-item-${it.id}`} className="card-warm !p-0 overflow-hidden">
                {isImage ? (
                  <a href={link} target="_blank" rel="noreferrer" className="block aspect-[4/3] bg-forest-900/5">
                    <img src={it.file_data} alt={it.title} className="w-full h-full object-cover" />
                  </a>
                ) : (
                  <div className="aspect-[4/3] flex items-center justify-center bg-forest-900/5 text-forest-800">
                    <Icon className="h-10 w-10" strokeWidth={1.2}/>
                  </div>
                )}
                <div className="p-5 space-y-3">
                  <h3 className="font-serif text-xl text-forest-900">{it.title}</h3>
                  {it.description && <p className="text-sm text-forest-700 leading-relaxed">{it.description}</p>}
                  {link && (
                    <a
                      href={link}
                      target="_blank"
                      rel="noreferrer"
                      download={it.file_data ? (it.title || "sunbraider") : undefined}
                      className="btn-secondary !py-2 !text-[11px]"
                      data-testid={`promo-download-${it.id}`}
                    >
                      <Download className="h-4 w-4" strokeWidth={1.5}/>
                      {it.file_type === "link" ? "Open" : "Download"}
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Testimonials */}
      <div className="mt-24" data-testid="promo-testimonials-section">
        <p className="eyebrow"><Quote className="inline h-3.5 w-3.5 mr-1" strokeWidth={1.5}/> Voices from sessions</p>
        <h2 className="mt-3 font-serif text-3xl md:text-4xl text-forest-900">What others carry with them</h2>
        {testimonials.length > 0 ? (
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((tt) => (
              <div key={tt.id} data-testid={`testimonial-${tt.id}`} className="card-warm !p-6">
                {typeof tt.rating === "number" && (
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`h-3.5 w-3.5 ${i < tt.rating ? "fill-wheat-500 text-wheat-500" : "text-forest-900/15"}`} strokeWidth={1.5}/>
                    ))}
                  </div>
                )}
                <p className="font-serif text-lg text-forest-900 leading-relaxed">“{tt.quote}”</p>
                <p className="mt-4 text-[11px] uppercase tracking-[0.28em] text-wheat-700">— {tt.name}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-6 text-sm text-forest-700 italic">First voices will appear here soon — thank you for your patience.</p>
        )}

        {/* Submission form */}
        <div className="mt-14 max-w-2xl" data-testid="testimonial-form-wrap">
          <p className="eyebrow">Share what you experienced</p>
          <h3 className="mt-2 font-serif text-2xl text-forest-900">Leave a short reflection</h3>
          <p className="mt-2 text-sm text-forest-700">A few honest words help others feel safe to book. I read every message before publishing.</p>
          {submitted ? (
            <div className="mt-6 card-warm" data-testid="testimonial-success">
              <p className="font-serif text-xl text-forest-900">Thank you 🙏</p>
              <p className="mt-2 text-sm text-forest-700">Your words are with me. Once approved, they'll appear on this page.</p>
            </div>
          ) : (
            <form onSubmit={submitTestimonial} className="mt-6 card-warm space-y-4" data-testid="testimonial-form">
              <div>
                <label className="field-label">Your first name</label>
                <input required maxLength={80} className="field-input" data-testid="testimonial-name" value={tf.name} onChange={(e) => setTf({ ...tf, name: e.target.value })} placeholder="Anna, Mees, Iryna…" />
              </div>
              <div>
                <label className="field-label">Your reflection</label>
                <textarea required rows={4} minLength={8} maxLength={800} className="field-input" data-testid="testimonial-quote" value={tf.quote} onChange={(e) => setTf({ ...tf, quote: e.target.value })} placeholder="A few words about how the session felt."/>
                <p className="mt-1 text-[10px] text-forest-700/70">{tf.quote.length}/800</p>
              </div>
              <div>
                <label className="field-label">Rating (optional)</label>
                <div className="flex items-center gap-1" data-testid="testimonial-rating">
                  {[1,2,3,4,5].map((n) => (
                    <button type="button" key={n} onClick={() => setTf({ ...tf, rating: n === tf.rating ? null : n })} data-testid={`testimonial-star-${n}`} aria-label={`${n} stars`}>
                      <Star className={`h-6 w-6 transition ${tf.rating && n <= tf.rating ? "fill-wheat-500 text-wheat-500" : "text-forest-900/25 hover:text-wheat-500"}`} strokeWidth={1.5} />
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm text-forest-700">
                <input type="checkbox" checked={tf.consent} onChange={(e) => setTf({ ...tf, consent: e.target.checked })} data-testid="testimonial-consent" className="mt-1"/>
                <span>I agree that Isa may publish these words on the Sunbraider website. My last name will not be shown.</span>
              </label>
              <button disabled={submitting} className="btn-primary" data-testid="testimonial-submit">
                <Send className="h-4 w-4" strokeWidth={1.5}/> {submitting ? "Sending…" : "Share reflection"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
